import { useTranslation } from 'react-i18next';

import { cn } from '@/utils/cn';

export type UnityLogoVariant =
  | 'symbol-color'
  | 'symbol-on-light'
  | 'symbol-on-dark'
  | 'horizontal-color'
  | 'horizontal-on-light'
  | 'horizontal-on-dark';

const SYMBOL_SRC = {
  color: '/brand/symbol-color.png',
  'on-light': '/brand/symbol-on-light.png',
  'on-dark': '/brand/symbol-on-dark.png',
} as const;

type SymbolTone = keyof typeof SYMBOL_SRC;

const symbolToneForVariant = (variant: UnityLogoVariant): SymbolTone => {
  if (variant.endsWith('-on-dark')) return 'on-dark';
  if (variant.endsWith('-on-light')) return 'on-light';
  return 'color';
};

export interface UnityLogoProps {
  variant?: UnityLogoVariant;
  className?: string;
  /** Accessible label. Omit when the logo sits beside visible product text. */
  label?: string;
}

/**
 * Unity platform mark from the official symbol export — not a redrawn icon.
 */
export const UnityLogo = ({
  variant = 'symbol-color',
  className,
  label,
}: UnityLogoProps) => {
  const { t } = useTranslation();

  const symbolTone = symbolToneForVariant(variant);
  const symbolSrc = SYMBOL_SRC[symbolTone];

  if (
    variant === 'horizontal-color' ||
    variant === 'horizontal-on-light' ||
    variant === 'horizontal-on-dark'
  ) {
    const onDark = variant === 'horizontal-on-dark';

    return (
      <div
        className={cn('flex items-center gap-3', className)}
        aria-label={label ?? `${t('app.name')} ${t('app.descriptor')}`}
        role={label ? undefined : 'img'}
      >
        <img
          src={symbolSrc}
          alt=""
          aria-hidden
          className="size-10 shrink-0 select-none sm:size-11"
          decoding="async"
        />
        <span className="min-w-0 text-left leading-none">
          <span
            className={cn(
              'block text-xl font-bold tracking-tight sm:text-2xl',
              onDark ? 'text-white' : 'text-fg',
            )}
          >
            {t('app.name')}
          </span>
          <span
            className={cn(
              'mt-1 block text-[0.6rem] font-semibold uppercase tracking-[0.28em] sm:text-[0.65rem]',
              onDark ? 'text-brand-accent' : 'text-fg-secondary',
            )}
          >
            {t('app.descriptor')}
          </span>
        </span>
      </div>
    );
  }

  return (
    <img
      src={symbolSrc}
      alt={label ?? ''}
      aria-hidden={label ? undefined : true}
      className={cn('block h-auto max-w-full select-none', className)}
      decoding="async"
    />
  );
};
